import React, { useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { platform } from '../config/runtime';
import NatureCapabilityShowcase from '../components/NatureCapabilityShowcase';
import LoadingIndicator from '../components/LoadingIndicator';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { getPostAuthDefaultRoute } from '../utils/onboarding';
import { partnerAPI } from '../services/api';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=ai.okio.app';

const WelcomePage = () => {
  const { t } = useTranslation('home');
  const useCaseRef = useRef(null);
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const isRealUser = Boolean(isAuthenticated) && user?.authProvider !== 'guest';

  // Venue owners live in their dashboard, not the B2C creation flow — for
  // them the bare domain means "my venue". Same query key as Layout's
  // "My venue" pill, so this is usually answered from cache instantly.
  const { data: myVenueResponse, isFetched: venueChecked } = useQuery({
    queryKey: ['partner-mine'],
    queryFn: () => partnerAPI.getMine(),
    enabled: isRealUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    // Guests (Scan-to-Play visitors) are authenticated but should still see
    // the landing page — it's their door into the bigger Okio world.
    if (!isRealUser) return;
    if (!venueChecked) return; // one beat to learn whether a venue exists
    const venue = myVenueResponse?.data?.data?.partner;
    navigate(venue ? `/p/${venue.slug}/dashboard` : getPostAuthDefaultRoute(user), {
      replace: true,
    });
  }, [isRealUser, venueChecked, myVenueResponse, navigate, user]);

  useEffect(() => {
    if (!isAuthenticated && platform.isNative()) {
      navigate('/login', {
        replace: true,
        state: { from: location.state?.from },
      });
    }
  }, [isAuthenticated, navigate, location.state?.from]);

  // Set body background and override safe-area padding for this page
  useEffect(() => {
    if (!isAuthenticated && platform.isNative()) return;
    const originalBg = document.body.style.backgroundColor;
    const originalHtmlPadding = document.documentElement.style.padding;

    document.body.style.backgroundColor = '#000';
    document.documentElement.style.padding = '0';

    return () => {
      document.body.style.backgroundColor = originalBg;
      document.documentElement.style.padding = originalHtmlPadding;
    };
  }, [isAuthenticated]);

  // The page scrolls inside a nested container (the document itself is
  // fixed), so spacebar / arrow keys would target the non-scrolling body.
  // Focusing the container makes keyboard scrolling work like on a normal
  // page.
  useEffect(() => {
    scrollRef.current?.focus({ preventScroll: true });
  }, []);

  if (platform.isNative()) {
    return null;
  }

  return (
    <div className="dark fixed inset-0 flex flex-col bg-black">
      {/* Header - NOT fixed, just at top of flex container */}
      <header className="flex-shrink-0 bg-black z-50">
        <div className="grid grid-cols-[auto,1fr,auto] items-center h-14 mx-auto px-8 pr-0 w-full gap-6 max-w-[1500px] mx-auto">
          <img
            src="/logo_white.webp"
            alt="OKIO"
            className="h-6 md:h-7 w-auto"
          />
          <div className="ml-auto flex items-center gap-3 md:gap-4">
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/starters"
                className="px-2 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
              >
                {t('nav.starters')}
              </Link>
              <Link
                to="/discover"
                className="px-2 py-2 text-sm font-medium text-white/70 hover:text-white transition-colors"
              >
                {t('nav.discover')}
              </Link>
              <Link
                to="/business"
                className="px-2 py-2 text-sm font-semibold text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                {t('nav.business')}
              </Link>
            </div>
            <LanguageSwitcher />
            <Link
              to="/login"
              state={{ from: location.state?.from || undefined }}
              className="text-sm font-semibold text-[#0a0a0a] bg-emerald-400 hover:bg-emerald-300 rounded-full px-4 py-2 transition-colors whitespace-nowrap"
            >
              {t('nav.signIn')}
            </Link>
            {/* <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs md:text-sm font-medium text-white hover:opacity-70 transition-opacity border-2 border-white/90 rounded-full px-3 md:px-4 py-1.5 md:py-2"
            >
              Get App
            </a> */}
          </div>
        </div>
      </header>

      {/* Scrollable content container */}
      <div ref={scrollRef} tabIndex={-1} className="flex-1 overflow-y-auto overflow-x-hidden outline-none">

      {/* ============================================
          SLIDE 1: Hero
          ============================================ */}
      <section className="relative min-h-[calc(100vh-52px)] flex items-end overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-right bg-no-repeat md:hidden" style={{ backgroundImage: `url('/home/forrest.webp')` }} />
        <div className="absolute inset-0 bg-cover bg-top bg-no-repeat hidden md:block" style={{ backgroundImage: `url('/home/forrest.webp')` }} />
        <div className="relative z-10 max-w-[1550px] mx-auto px-0 md:px-6 pb-0 pt-8 w-full">
          <div className="max-w-2xl">
            <div className="px-8 pt-8 md:pt-10 pb-20 md:pb-8 lg:p-10 bg-black/60 backdrop-blur-md md:border-4 border-2 border-b-0 md:border-b-0 border-white rounded-t-[40px] flex flex-col">
              {/* Eyes + logo lockup */}
              <div className="flex items-center gap-3 mb-8 md:mb-10 mt-2">
                <div className="scale-[1.8] md:scale-[2.4] origin-left transform-gpu">
                  <LoadingIndicator className="!mt-0 !py-0 !px-0" />
                </div>
              </div>

              <h1 className="text-[2.3rem] uppercase sm:text-5xl lg:text-6xl font-bold text-white leading-[1.0] mb-5 tracking-tight">
                {t('hero.title')}
              </h1>
              <p className="text-[1.1rem] lg:text-2xl font-medium text-white/80 mb-8 leading-relaxed">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-nowrap gap-2 lg:gap-3">
                <Link
                  to="/starters"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-2xl bg-emerald-400 px-3 py-3.5 text-sm font-semibold uppercase text-[#1a1a18] transition-colors hover:bg-emerald-300 sm:gap-2.5 sm:px-6 md:text-base"
                >
                  <svg className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                  </svg>
                  {t('hero.ctaExplore')}
                </Link>
                <a
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-2xl border-2 border-white/30 px-3 py-3.5 text-sm font-semibold uppercase text-white transition-colors hover:border-white/60 sm:gap-2.5 sm:px-6 md:text-base"
                >
                  <svg className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  {t('hero.ctaAndroid')}
                </a>
              </div>

              {/* Quiet B2B door, kept visually subordinate to the consumer
                  CTAs so it costs the main conversion nothing. */}
              <p className="mt-6 text-sm text-white/50">
                {t('hero.b2bPrompt')}{' '}
                <Link to="/business" className="font-medium text-emerald-400 hover:text-emerald-300">
                  {t('hero.b2bLink')}
                </Link>
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* ============================================
          SLIDES 2-5: Capability Showcase
          ============================================ */}

      <div ref={useCaseRef}>
        <NatureCapabilityShowcase />
      </div>

      {/* Final CTA */}
      <section className="relative py-32 md:py-40 px-6">
        <div className="absolute inset-0">
          <img
            src="/home/forrest2.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="relative max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight font-bold max-w-2xl mx-auto">
            {t('finalCta.title')}
          </h2>
          <p className="text-lg md:text-xl mb-12 text-white/80 max-w-xl mx-auto">
            {t('finalCta.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center flex-wrap">
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a1a18] hover:bg-white/90 font-medium rounded-xl transition-colors text-base min-w-[180px] justify-center"
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
              </svg>
              {t('finalCta.ctaAndroid')}
            </a>
            <Link
              to="/starters"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#1a1a18] hover:bg-white/90 font-medium rounded-xl transition-colors text-base min-w-[180px] justify-center"
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
              {t('finalCta.ctaBrowser')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer - Minimal */}
      <footer className="py-12 px-6 bg-[#1a1a18] text-white/60">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <img
            src="/logo_white.webp"
            alt="OKIO"
            className="h-6 w-auto opacity-60"
          />
          <p className="text-sm text-center md:text-right">
            {t('footer.prompt')}{' '}
            <Link to="/business" className="text-emerald-400 hover:text-emerald-300 font-medium">
              {t('footer.businessLink')}
            </Link>
            <span className="block md:inline md:ml-3 text-white/40">hello@okio.ai</span>
          </p>
        </div>
      </footer>
      </div>{/* End scrollable container */}
    </div>
  );
};

export default WelcomePage;
